import React from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Form,
    Input,
    Row,
    Select,
    Upload,
    Progress,
} from 'antd';
import type { UploadFile } from 'antd';
import { SettingOutlined, UploadOutlined } from '@ant-design/icons';
import { FirmwareApi } from '../../../services/Api';
import {
    DisplayOptions,
    FIRMWARE_TYPE,
    FIRMWARE_VERSION,
    ROLE,
} from '../../../services/Macro';
type DisplayOption = (typeof DisplayOptions)[keyof typeof DisplayOptions];

interface FmPanelProps {
    confirm: string;
    trans: (key: string) => string;
    handleError: (title: string, info: string) => void;
    updateFileList: (upFm: boolean, upApp: boolean) => void;
    notify: (message: string) => void;
}

interface FmPanelState {
    fmPanelDisplay: DisplayOption;
    versionNo: string;
    type: string;
    version: string;
    appTypes: string[];
    progress: number;
    fileList: UploadFile[];
}

interface FormValues {
    versionNo: string;
}

export default class FmPanel extends React.Component<
    FmPanelProps,
    FmPanelState
> {
    state: FmPanelState = {
        fmPanelDisplay: DisplayOptions.block,
        versionNo: '',
        type: 'CA',
        version: 'Beta',
        appTypes: [],
        progress: 0,
        fileList: [],
    };

    switchFmUpgradePanel = () => {
        this.setState((prevState) => ({
            fmPanelDisplay:
                prevState.fmPanelDisplay === DisplayOptions.none
                    ? DisplayOptions.block
                    : DisplayOptions.none,
        }));
    };

    callApi = async (values: FormValues) => {
        const {
            trans: t,
            handleError,
            notify,
            updateFileList,
        } = this.props;

        const {
            appTypes,
            type,
            version,
            fileList,
        } = this.state;

        if (appTypes.length === 0) {
            handleError(
                t('localError'),
                t('appTypeErr')
            );
            return;
        }

        if (!values.versionNo) {
            handleError(
                t('localError'),
                t('versionNoNull')
            );
            return;
        }

        if (
            fileList.length === 0 ||
            !fileList[0].originFileObj
        ) {
            handleError(
                t('localError'),
                t('upgradeFileNull')
            );
            return;
        }

        const token =
            localStorage.getItem('token') || '';

        const lang =
            localStorage.getItem('lang') || 'zh_CN';

        const formData = new FormData();

        formData.append(
            'token',
            token
        );

        formData.append(
            'role',
            String(ROLE.SUPER)
        );

        formData.append(
            'firmwareType',
            String(
                (FIRMWARE_TYPE as Record<string, unknown>)[type]
            )
        );

        formData.append(
            'firmwareVersion',
            String(
                (FIRMWARE_VERSION as Record<string, unknown>)[version]
            )
        );

        formData.append(
            'appType',
            `[${appTypes}]`
        );

        formData.append(
            'versionNo',
            values.versionNo
        );

        formData.append(
            'file',
            fileList[0].originFileObj
        );

        formData.append(
            'lang',
            lang.split('_')[0]
        );

        try {
            const response = await axios.post(
                FirmwareApi.SUPER_UPGRADE,
                formData,
                {
                    headers: {
                        'Content-Type':
                            'multipart/form-data',
                    },

                    onUploadProgress: (event) => {
                        if (event.total) {
                            this.setState({
                                progress: Math.round(
                                    (event.loaded /
                                        event.total) *
                                    100
                                ),
                            });
                        }
                    },
                }
            );

            if (response.data.code === 400) {
                handleError(
                    t('serverError'),
                    response.data.message
                );
                return;
            }

            this.setState({
                progress: 0,
                fileList: [],
            });

            notify(
                t('upgradeSuccess')
            );

            updateFileList(
                true,
                false
            );
        } catch (error: any) {
            this.setState({
                progress: 0,
            });

            handleError(
                t('serverError'),
                error?.response?.data?.message ||
                error?.message ||
                String(error)
            );
        }
    };

    render() {
        const {
            confirm,
            trans: t,
        } = this.props;

        const {
            fmPanelDisplay,
            type,
            version,
            appTypes,
            progress,
            fileList,
        } = this.state;

        return (
            <Card
                title={t('firmwareUpgrade')}
                extra={
                    <Button
                        type="text"
                        icon={
                            <SettingOutlined />
                        }
                        onClick={
                            this.switchFmUpgradePanel
                        }
                    />
                }
                style={{
                    marginBottom: 16,
                }}
            >
                {fmPanelDisplay !==
                    DisplayOptions.none && (
                        <Form<FormValues>
                            layout="vertical"
                            onFinish={
                                this.callApi
                            }
                        >
                            <Row gutter={16}>
                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <Form.Item
                                        label={t(
                                            'firmwareType'
                                        )}
                                    >
                                        <Select
                                            value={type}
                                            onChange={(
                                                value
                                            ) =>
                                                this.setState(
                                                    {
                                                        type: value,
                                                    }
                                                )
                                            }
                                            options={[
                                                {
                                                    value: 'CA',
                                                    label: 'CA',
                                                },
                                                {
                                                    value: 'CB',
                                                    label: 'CB',
                                                },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <Form.Item
                                        label={t(
                                            'firmwareVersion'
                                        )}
                                    >
                                        <Select
                                            value={version}
                                            onChange={(
                                                value
                                            ) =>
                                                this.setState(
                                                    {
                                                        version:
                                                            value,
                                                    }
                                                )
                                            }
                                            options={[
                                                {
                                                    value: 'Beta',
                                                    label: 'Beta',
                                                },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col span={24}>
                                    <Form.Item
                                        label={t(
                                            'appVersion'
                                        )}
                                    >
                                        <Checkbox.Group
                                            value={
                                                appTypes
                                            }
                                            options={[
                                                {
                                                    value: '1',
                                                    label: 'dev',
                                                },
                                                {
                                                    value: '2',
                                                    label: 'alpha',
                                                },
                                                {
                                                    value: '3',
                                                    label: 'beta',
                                                },
                                                {
                                                    value: '4',
                                                    label: 'pro',
                                                },
                                            ]}
                                            onChange={(
                                                values
                                            ) =>
                                                this.setState(
                                                    {
                                                        appTypes:
                                                            values as string[],
                                                    }
                                                )
                                            }
                                        />
                                    </Form.Item>
                                </Col>

                                <Col span={24}>
                                    <Form.Item
                                        name="versionNo"
                                        label={t(
                                            'versionNo'
                                        )}
                                        rules={[
                                            {
                                                required: true,
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>

                                <Col span={24}>
                                    <Upload
                                        maxCount={1}
                                        fileList={
                                            fileList
                                        }
                                        beforeUpload={() =>
                                            false
                                        }
                                        onChange={({
                                            fileList:
                                            nextFileList,
                                        }) =>
                                            this.setState(
                                                {
                                                    fileList:
                                                        nextFileList,
                                                }
                                            )
                                        }
                                    >
                                        <Button
                                            icon={
                                                <UploadOutlined />
                                            }
                                        >
                                            Select File
                                        </Button>
                                    </Upload>

                                    <Progress
                                        percent={
                                            progress
                                        }
                                        status={
                                            progress ===
                                                100
                                                ? 'success'
                                                : 'active'
                                        }
                                    />
                                </Col>

                                <Col
                                    span={24}
                                    style={{
                                        textAlign:
                                            'right',
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                    >
                                        {confirm}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    )}
            </Card>
        );
    }
}